import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpUrlEncodingCodec } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IRemoveAudienceUsersRequest } from 'app/library/audience-user/interfaces/remove-audience-users-request.interface';
import { IAudienceRelevantExperienceQuery } from 'app/library/grow/interfaces/audience-relevant-experience.model.interface';
import { IAudienceFilters } from 'app/library/grow/interfaces/audience-filters.interface';
import { IAudienceFiltersPayload } from 'app/library/grow/interfaces/audience-filters-payload.interface';
import { IAudienceMembersRequestOptions } from 'app/library/grow/interfaces/audience-members-request.interface';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { IUserQuery } from 'app/projects/user/src/lib/interfaces/user.query.interface';
import { peekAsyncJobIdFromResponse } from 'app/services/http-async-job-response.util';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AudienceExperienceStatusEnum } from '../enums/audience-experience-status.enum';
import { IAudienceWidgetQuery } from '../interfaces/audience-widget.query';
import { IAudienceQuery } from '../interfaces/audience.query.interface';
import { AudienceMoveToCommand } from '../models/audience-moveTo.command.model';
import { AudienceUpdateCommand } from '../models/audience-update.command.model';
import { AudienceWidget } from '../models/audience-widget.model';
import { AudienceCommand } from '../models/audience.command.model';
import { MaxBrainAudience } from '../models/audience.model';

/** Preserves `filter[...]` bracket keys in query strings (matches AllUsers API usage). */
class BracketPreservingHttpUrlEncodingCodec extends HttpUrlEncodingCodec {
    override encodeKey(key: string): string {
        return super.encodeKey(key).replace(/%5B/g, '[').replace(/%5D/g, ']');
    }
}

export interface IDefaultAudiencesAndTotalCount {
    data: IAudienceQuery[];
    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}

/** Optional query flags for `root`, `search`, and `{id}/children` audience list calls */
export interface AudienceListRequestOptions {
    /** Adds `context=defaultSetting` and optional `filter[experienceId]` */
    context?: 'defaultSetting';
    experienceId?: string | null;
    /** Excludes audience types from list results (1 = Normal, 2 = Community, 3 = Automatic) */
    excludeTypes?: number[];
}

export const EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS: AudienceListRequestOptions = {
    excludeTypes: [3],
};

@Injectable()
export class AudienceApiService extends EntityService<IAudienceQuery, AudienceCommand> implements IEntityApiService<MaxBrainAudience> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'engage/audiences');
    }

    getAll(search?: string, listOptions?: AudienceListRequestOptions): Observable<MaxBrainAudience[]> {
        const params = this._buildListParams(listOptions, search);

        if (search) {
            return this.http
                .get<IAudienceQuery[]>(`${this.apiUrl}/${this.entities}/search`, { params: params ?? new HttpParams().set('search', search) })
                .pipe(map((queries) => queries.map((query) => new MaxBrainAudience(query))));
        }

        return this.http
            .get<IAudienceQuery[]>(`${this.apiUrl}/${this.entities}/root`, params ? { params } : {})
            .pipe(map((queries) => queries.map((query) => new MaxBrainAudience(query))));
    }

    getAudienceChildren(audienceId: string, listOptions?: AudienceListRequestOptions): Observable<MaxBrainAudience[]> {
        const params = this._buildListParams(listOptions);

        return this.http
            .get<IAudienceQuery[]>(`${this.apiUrl}/${this.entities}/${audienceId}/children`, params ? { params } : {})
            .pipe(map((queries) => queries.map((query) => new MaxBrainAudience(query))));
    }

    create(entity: MaxBrainAudience): Observable<MaxBrainAudience> {
        const data = new AudienceCommand(entity);

        return this._create(data).pipe(map((query) => new MaxBrainAudience(query)));
    }

    read(id: string): Observable<MaxBrainAudience> {
        return this._read(id).pipe(map((query) => new MaxBrainAudience(query)));
    }

    fetchAudienceWidgets(audienceId: string): Observable<AudienceWidget[]> {
        return this.http
            .get<IAudienceWidgetQuery[]>(`${this.apiUrl}/${this.entities}/${audienceId}/widgets`)
            .pipe(map((queries) => queries.map((query) => new AudienceWidget(query))));
    }

    fetchAudienceRelevantExperiences(audienceId: string, status?: AudienceExperienceStatusEnum): Observable<AudienceRelevantExperience[]> {
        let params = new HttpParams();
        // Use != null so 0 is allowed, but null/undefined are ignored
        if (status != null) {
            params = params.set('filter[status]', String(status));
        }

        return this.http
            .get<IAudienceRelevantExperienceQuery[]>(`${this.apiUrl}/${this.entities}/${audienceId}/experiences`, { params })
            .pipe(map((queries) => queries.map((query) => new AudienceRelevantExperience(query))));
    }

    fetchAudienceFilters(audienceId: string): Observable<IAudienceFilters> {
        return this.http.get<IAudienceFilters>(`${this.apiUrl}/${this.entities}/${audienceId}/filters`);
    }

    updateAudienceFilters(audienceId: string, filters: IAudienceFiltersPayload): Observable<IAudienceFilters> {
        return this.http.put<IAudienceFilters>(`${this.apiUrl}/${this.entities}/${audienceId}/filters`, filters);
    }

    getAudienceMembers(audienceId: string, options: IAudienceMembersRequestOptions = {}): Observable<IPaginatedResponse<IUserQuery>> {
        let params = new HttpParams({ encoder: new BracketPreservingHttpUrlEncodingCodec() })
            .set('page', String(options.page ?? 1))
            .set('limit', String(options.limit ?? 10));

        if (options.query) {
            params = params.set('filter[query]', options.query);
        }

        if (options.sortBy && options.sortOrder) {
            params = params.set('sortBy', options.sortBy).set('sortOrder', options.sortOrder.toUpperCase());
        }

        return this.http.get<IPaginatedResponse<IUserQuery>>(`${this.apiUrl}/${this.entities}/${audienceId}/members`, { params });
    }

    removeAudienceUsers(audienceId: string, payload: IRemoveAudienceUsersRequest): Observable<{ asyncJobId?: string }> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: payload,
            observe: 'response' as const,
        };

        return this.http.delete<void>(`${this.apiUrl}/${this.entities}/${audienceId}/users`, options).pipe(
            map((resp: HttpResponse<void>) => {
                const asyncJobId = peekAsyncJobIdFromResponse(resp);
                return asyncJobId ? { asyncJobId } : {};
            })
        );
    }

    updateAudienceRelevantExperienceStatus(audienceId: string, experienceId: string, status: AudienceExperienceStatusEnum): Observable<AudienceRelevantExperience> {
        return this.http
            .patch<IAudienceRelevantExperienceQuery>(`${this.apiUrl}/${this.entities}/${audienceId}/experiences/${experienceId}`, { status })
            .pipe(map((query) => new AudienceRelevantExperience(query)));
    }

    update(entity: MaxBrainAudience): Observable<MaxBrainAudience> {
        const data = new AudienceUpdateCommand(entity);
        return this.http.patch<IAudienceQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new MaxBrainAudience(query)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    moveTo(current: MaxBrainAudience, target: MaxBrainAudience): Observable<MaxBrainAudience> {
        const data = new AudienceMoveToCommand(target);
        return this.http.patch<IAudienceQuery>(`${this.apiUrl}/${this.entities}/${current.id}`, data).pipe(map((query) => new MaxBrainAudience(query)));
    }

    getDefaultAudiences(pageSize = 10, pageIndex = 1): Observable<IDefaultAudiencesAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        return this.http.get<IDefaultAudiencesAndTotalCount>(`${this.apiUrl}/engage/audiences/default`, { params });
    }

    private _buildListParams(listOptions?: AudienceListRequestOptions, search?: string): HttpParams | null {
        let params = new HttpParams({ encoder: new BracketPreservingHttpUrlEncodingCodec() });
        let hasParams = false;

        if (listOptions?.context === 'defaultSetting') {
            params = params.set('context', 'defaultSetting');
            hasParams = true;

            if (listOptions.experienceId) {
                params = params.set('filter[experienceId]', listOptions.experienceId);
            }
        }

        listOptions?.excludeTypes?.forEach((type) => {
            params = params.append('filter[excludeTypes]', String(type));
            hasParams = true;
        });

        if (search) {
            params = params.set('search', search);
            hasParams = true;
        }

        return hasParams ? params : null;
    }

    patchAudienceDefaultFlags(audienceIds: string[], isDefault: boolean): Observable<unknown> {
        const audience_ids = audienceIds.map((id) => Number(id));
        return this.http.patch(`${this.apiUrl}/${this.entities}/default`, { audience_ids, is_default: isDefault });
    }
}
